import React, { useState, useEffect } from 'react';
import { ShieldAlert, AlertCircle } from 'lucide-react';
import { db } from '@infrastructure/firebase/FirebaseConfig';

// Inline check in case infrastructure is not fully ready
const isFirestoreBlocked = async () => {
    try {
        const response = await fetch('https://firestore.googleapis.com/google.firestore.v1.Firestore/Listen/channel', {
            method: 'OPTIONS',
            mode: 'no-cors'
        });
        return false;
    } catch (e) {
        return true;
    }
};

interface FirebaseErrorScreenProps {
  onSkipLogin: () => void;
  errorMessage?: string;
}

/**
 * FirebaseErrorScreen - Displayed when Firestore connectivity is blocked or fails.
 */
export const FirebaseErrorScreen: React.FC<FirebaseErrorScreenProps> = ({ 
  onSkipLogin, 
  errorMessage 
}) => {
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    isFirestoreBlocked().then(blocked => {
      if (blocked) setIsBlocked(true);
    });
  }, []);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6 bg-[#020617] text-white overflow-hidden relative">
      {/* Background Pulse Effect */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-rose-600/10 blur-[130px] rounded-full" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="entrance-glass border border-white/10 rounded-[2.5rem] shadow-2xl p-10 text-center relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-rose-500 to-transparent" />
          
          <div className="inline-flex items-center justify-center w-20 h-20 bg-rose-500/10 border border-rose-500/20 rounded-3xl mb-8 shadow-[0_0_30px_rgba(244,63,94,0.2)]">
            <ShieldAlert className="text-rose-500" size={40} />
          </div>
          
          <h1 className="text-2xl font-black text-white uppercase tracking-[0.2em] mb-3">
            {isBlocked ? 'Truy cập bị hạn chế' : 'Mất đồng bộ nút'}
          </h1>
          <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.3em] mb-10 leading-relaxed">
            {isBlocked ? 'Giao thức an ninh mạng đang chặn đồng bộ hóa nút' : 'Bắt tay mã hóa thất bại trong phân khu hiện tại'}
          </p>

          {(errorMessage || isBlocked) && (
            <div className="bg-rose-500/5 border border-rose-500/20 rounded-2xl p-6 mb-10 text-left">
              <p className="text-rose-400 text-[9px] font-black uppercase tracking-widest mb-3 flex items-center gap-2">
                 <AlertCircle size={14} /> Chi tiết cảnh báo an ninh
              </p>
              <p className="text-slate-300 text-xs font-bold leading-relaxed">
                {isBlocked
                   ? 'Nút cục bộ không thể thiết lập kết nối với firestore.googleapis.com. Điều này thường do phần mềm chặn quảng cáo (Ad-blocker) hoặc các quy tắc tường lửa nghiêm ngặt. Vui lòng tạm thời vô hiệu hóa Ad-blocker cho địa chỉ http://localhost:5173 và tải lại trang.'
                   : (errorMessage || 'Bắt tay mã hóa thất bại trong phân khu hiện tại')}
              </p>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={() => window.location.reload()}
              className="w-full h-16 bg-indigo-600 hover:bg-indigo-700 rounded-2xl font-black text-white uppercase tracking-[0.2em] text-xs transition-all shadow-xl shadow-indigo-600/20"
            >
              Tái đồng bộ nút lõi
            </button>
            <button
              onClick={onSkipLogin}
              className="w-full text-slate-600 hover:text-slate-400 font-black text-[10px] uppercase tracking-[0.3em] transition-colors pt-2"
            >
              Tiếp tục qua Bộ nhớ cục bộ (Ngoại tuyến)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirebaseErrorScreen;
